/**
 * Per-community map of member_id -> current contributor role (the recent-window
 * quadrant, matching the members table), loaded once per selected community so any
 * Avatar can wear a role-coloured ring without the quadrant being threaded through
 * every payload.
 *
 * Values are role keys (see @ui/quadrant), including the terminal `departed` — a
 * member who has left carries it instead of a quadrant, so their avatar wears the
 * muted departed ring rather than losing its ring entirely.
 *
 * ON `GET /v1/communities/{id}/roles` (doc 04 §3.3 row 16). The legacy
 * `/admin/analytics/member-roles` stays mounted — it is FROZEN for the Helper, which
 * issued 3,462 requests against it from confirmed installs in 14 days (doc 07 §2.3) —
 * but the web app reads the v1 twin. Both are the same `windowed_role_map_cached`
 * map, so a community's rings agree across the panel and the extension.
 *
 * The keys are decimal member ids AS STRINGS, on both surfaces, which is what
 * `roleOf`'s `String(memberId)` lookup depends on. v1 emits them in member-id order
 * where the legacy route's order varied between identical calls; nothing here reads
 * the order.
 */
import { fetchRoles } from '@api';

const cache = new Map<number, Record<string, string>>();

export const roles = $state<{ skoolId: number | null; map: Record<string, string> }>({
  skoolId: null,
  map: {},
});

let loading: number | null = null;

/** Load (once, cached) the role map for a community. Safe to call repeatedly. */
export async function ensureRoles(skoolId: number | null): Promise<void> {
  if (skoolId == null || roles.skoolId === skoolId || loading === skoolId) return;
  const cached = cache.get(skoolId);
  if (cached) {
    roles.skoolId = skoolId;
    roles.map = cached;
    return;
  }
  loading = skoolId;
  try {
    const r = await fetchRoles(skoolId);
    cache.set(skoolId, r.roles);
    roles.skoolId = skoolId;
    roles.map = r.roles;
  } catch {
    /* non-critical — avatars just render without a ring */
  } finally {
    if (loading === skoolId) loading = null;
  }
}

/** This member's role for the currently-loaded community, or null. Reactive. */
export function roleOf(memberId: number | null | undefined): string | null {
  return memberId == null ? null : (roles.map[String(memberId)] ?? null);
}
